import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { MicroSentryModule } from '@micro-sentry/angular';
import { environment } from '../environments/environment';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginComponent } from './login/login.component';
import { HomeComponent } from './home/home.component';
import { AuthGuard } from './services/auth.guard';
import { RouterModule } from '@angular/router';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { UsersComponent } from './users/users.component';
import { EmployeesComponent } from './employees/employees.component';
import { ProfileComponent } from './profile/profile.component';
import { SalaryComponent } from './salary/salary.component';
import { AdvanceComponent } from './salary/advance/advance.component';
import { DatePipe } from '@angular/common';
import { DoctorsComponent } from './doctors/doctors.component';
import { PatientsComponent } from './patients/patients.component';
import { ForgetPasswordComponent } from './login/forget-password/forget-password.component';
import { AppointmentComponent } from './appointment/appointment.component';
import { DetailsComponent } from './patients/details/details.component';
import { AttendanceComponent } from './employees/attendance/attendance.component';
import { EvaluationComponent } from './employees/evaluation/evaluation.component';
import { EvaluationFormComponent } from './evaluations/evaluation-form/evaluation-form.component';
import { AppointementDetailsComponent } from './appointment/appointement-details/appointement-details.component';
import { SettingsComponent } from './settings/settings.component';
import { CalendarComponent } from './doctors/calendar/calendar.component';
import { DoctorAppointmentsPageComponent } from './doctor-appointments-page/doctor-appointments-page.component';
import { DetailsViewComponent } from './appointment/details-view/details-view.component';
import { InvoicesComponent } from './invoices/invoices.component';
import { PrintComponent } from './invoices/print/print.component';
import { MatPaginatorModule } from '@angular/material/paginator';
import { ReportsComponent } from './reports/reports.component';
import { GraphReportComponent } from './reports/graph-report/graph-report.component';
import { PackagesComponent } from './packages/packages.component';
import { OffersComponent } from './offers/offers.component';
import { ItemsComponent } from './items/items.component';
import { StaffSettingsComponent } from './staff-settings/staff-settings.component';
import { UsersPackagesComponent } from './users-packages/users-packages.component';
import { TimeFormatPipe } from './pipes/time-format.pipe';
import { NotesHistoryComponent } from './notes-history/notes-history.component';
import { PendingsComponent } from './appointment/pendings/pendings.component';
import { StoreComponent } from './store/store.component';
import { PrintNotesComponent } from './appointment/print-notes/print-notes.component';

@NgModule({ declarations: [
        AppComponent,
        LoginComponent,
        HomeComponent,
        UsersComponent,
        EmployeesComponent,
        ProfileComponent,
        SalaryComponent,
        AdvanceComponent,
        DoctorsComponent,
        PatientsComponent,
        ForgetPasswordComponent,
        AppointmentComponent,
        DetailsComponent,
        AttendanceComponent,
        EvaluationComponent,
        EvaluationFormComponent,
        AppointementDetailsComponent,
        SettingsComponent,
        CalendarComponent,
        DoctorAppointmentsPageComponent,
        DetailsViewComponent,
        InvoicesComponent,
        PrintComponent,
        ReportsComponent,
        GraphReportComponent,
        PackagesComponent,
        OffersComponent,
        ItemsComponent,
        StaffSettingsComponent,
        UsersPackagesComponent,
        TimeFormatPipe,
        NotesHistoryComponent,
        PendingsComponent,
        StoreComponent,
        PrintNotesComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        AppRoutingModule,
        NgbModule,
        FormsModule,
        MatPaginatorModule,
        MicroSentryModule.forRoot({
            dsn: environment.glitchtipDsn || undefined,
            environment: environment.production ? 'production' : 'development',
            release: environment.glitchtipRelease || undefined,
        }),
        RouterModule.forRoot([
            { path: '', redirectTo: '/login', pathMatch: 'full' },
            { path: 'login', component: LoginComponent },
            { path: 'forget', component: ForgetPasswordComponent },
            { path: 'home', component: HomeComponent, canActivate: [AuthGuard], data: { roles: ['admin', 'doctor', 'staff', 'accountant'] } },
            { path: 'appointments', component: AppointmentComponent, canActivate: [AuthGuard], data: { roles: ['admin', 'doctor', 'staff', 'accountant'] } },
            { path: 'store', component: StoreComponent, canActivate: [AuthGuard], data: { roles: ['admin', 'doctor', 'staff', 'accountant'] } },
            { path: 'appointment_details', component: AppointementDetailsComponent, canActivate: [AuthGuard], data: { roles: ['admin', 'doctor'] } },
            { path: 'pending-appointments', component: PendingsComponent, canActivate: [AuthGuard], data: { roles: ['admin'] } },
            { path: 'view_details', component: DetailsViewComponent, canActivate: [AuthGuard], data: { roles: ['admin', 'doctor', 'staff', 'accountant'] } },
            { path: 'profile', component: ProfileComponent, canActivate: [AuthGuard], data: { roles: ['admin', 'doctor', 'staff', 'accountant'] } },
            { path: 'doctors', component: DoctorsComponent, canActivate: [AuthGuard], data: { roles: ['admin', 'doctor', 'staff', 'accountant'] } },
            { path: 'doctor-calendar', component: CalendarComponent, canActivate: [AuthGuard], data: { roles: ['admin', 'doctor', 'staff', 'accountant'] } },
            { path: 'patients', component: PatientsComponent, canActivate: [AuthGuard], data: { roles: ['admin', 'doctor', 'staff', 'accountant'] } },
            { path: 'patient_details', component: DetailsComponent, canActivate: [AuthGuard], data: { roles: ['admin', 'doctor', 'staff', 'accountant'] } },
            { path: 'invoices', component: InvoicesComponent, canActivate: [AuthGuard], data: { roles: ['admin', 'doctor', 'staff', 'accountant'] } },
            { path: 'invoice_print', component: PrintComponent, canActivate: [AuthGuard], data: { roles: ['admin', 'doctor', 'staff', 'accountant'] } },
            { path: 'print-notes', component: PrintNotesComponent, canActivate: [AuthGuard], data: { roles: ['admin', 'doctor', 'staff', 'accountant'] } },
            { path: 'history_notes_patient', component: NotesHistoryComponent, canActivate: [AuthGuard], data: { roles: ['admin', 'doctor', 'staff', 'accountant'] } },
            { path: 'users', component: UsersComponent, canActivate: [AuthGuard], data: { roles: ['admin'] } },
            { path: 'doctorHomePage', component: DoctorAppointmentsPageComponent, canActivate: [AuthGuard], data: { roles: ['doctor'] } },
            { path: 'employees', component: EmployeesComponent, canActivate: [AuthGuard], data: { roles: ['admin'] } },
            { path: 'salary', component: SalaryComponent, canActivate: [AuthGuard], data: { roles: ['admin'] } },
            { path: 'advance_salary', component: AdvanceComponent, canActivate: [AuthGuard], data: { roles: ['admin'] } },
            { path: 'attendance', component: AttendanceComponent, canActivate: [AuthGuard], data: { roles: ['admin'] } },
            { path: 'employee-evaluations', component: EvaluationComponent, canActivate: [AuthGuard], data: { roles: ['admin'] } },
            { path: 'settings', component: SettingsComponent, canActivate: [AuthGuard], data: { roles: ['admin'] } },
            { path: 'reports', component: ReportsComponent, canActivate: [AuthGuard], data: { roles: ['admin'] } },
            { path: 'graph-report', component: GraphReportComponent, canActivate: [AuthGuard], data: { roles: ['admin'] } },
            { path: 'packages', component: PackagesComponent, canActivate: [AuthGuard], data: { roles: ['admin'] } },
            { path: 'user-packages', component: UsersPackagesComponent, canActivate: [AuthGuard], data: { roles: ['admin'] } },
            { path: 'offers', component: OffersComponent, canActivate: [AuthGuard], data: { roles: ['admin'] } },
            { path: 'items', component: ItemsComponent, canActivate: [AuthGuard], data: { roles: ['admin'] } },
            { path: 'staff-settings', component: StaffSettingsComponent, canActivate: [AuthGuard], data: { roles: ['admin'] } }
        ], { scrollPositionRestoration: 'enabled' })], providers: [DatePipe, provideHttpClient(withInterceptorsFromDi())] })
export class AppModule { }
